// The visits schedule from the Sync Schedule prototype (COM-491): an hour-by-hour day,
// one recording at a time that patients can join or leave, and the day's work counted down.
import React, { useEffect, useRef, useState } from 'react';
import AudioRecorder from '../../api/Recorder';
import AddVisitSheet from './AddVisitSheet';
import LoadDaySheet from './LoadDaySheet';
import {
  addDays,
  clockLabel,
  DEFAULT_VISIT_MIN,
  isSameDay,
  startOfDay,
  visitsOnDay,
  visitTitle,
  visitTypeOf,
} from './visitsDay';
import {
  CalPlus,
  Caret,
  Check,
  Chev,
  Density,
  Eq,
  Grip,
  Jump,
  Mic,
  MicAdd,
  Move,
  Open,
  Pencil,
  People,
  Spark,
  Stop,
  SyncIcon,
  Trash,
} from './icons';
import './VisitsSchedule.css';

const DAY_FIRST = 7 * 60;
const DAY_LAST = 18 * 60;
const GAP_FOLD = 4; // empty hours in a row before they fold to one line
const MAX_IN_RECORDING = 10;
const STATUS_LABEL = {
  draft: 'Ready to Comprehend',
  done: 'Note filled',
  saving: 'Transcribing',
  writing: 'Comprehending',
};

const who = (c) => visitTitle(c) || 'No patient yet';
const DENSITY_KEY = 'vs-density';
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const nowMinutes = () => new Date().getHours() * 60 + new Date().getMinutes();

function gapRange(a, b) {
  const ca = clockLabel(a);
  const cb = clockLabel(b);
  return `${ca.slice(-2) === cb.slice(-2) ? ca.slice(0, -3) : ca} – ${cb}`;
}

function dayTitle(day) {
  const today = new Date();
  const rel = isSameDay(day, today)
    ? 'Today'
    : isSameDay(day, addDays(today, 1))
      ? 'Tomorrow'
      : isSameDay(day, addDays(today, -1))
        ? 'Yesterday'
        : '';
  const date = day.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  return { rel, date };
}

// Chrome lists the system default twice: as 'default' and under its own id.
function micChoices({ devices = [], selectedId }) {
  const def = devices.find((d) => d.deviceId === 'default');
  const real = devices.filter(
    (d) => d.deviceId !== 'default' && d.deviceId !== 'communications',
  );
  const sel =
    !selectedId || selectedId === 'default'
      ? real.find((d) => def && d.label === def.label)?.deviceId
      : selectedId;
  return real.map((d) => ({
    ...d,
    isDefault: !!def && d.label === def.label,
    selected: d.deviceId === sel,
  }));
}
const micLabel = (mics) =>
  micChoices(mics).find((d) => d.selected)?.label || 'Microphone';

const isoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const VisitsSchedule = ({
  conversations,
  day,
  onDayChange,
  sync,
  status,
  onOpen,
  patients,
  onCreateVisit,
  onParseDay,
  onReschedule,
  onDelete,
  onRefresh,
  canComprehend,
  comprehendingId,
  onComprehend,
}) => {
  const [openFolds, setOpenFolds] = useState({});
  const [openGaps, setOpenGaps] = useState({});
  const [menu, setMenu] = useState(null); // { id, left, top }
  const [delId, setDelId] = useState(null);
  const [arrange, setArrange] = useState(false);
  const [loadDay, setLoadDay] = useState(false);
  const [ghost, setGhost] = useState(null); // edit-times drag: { x, y, name, min }
  const [pills, setPills] = useState({ now: null, early: 0, top: 0 });
  const [addSheet, setAddSheet] = useState(null); // { startMin, durationMin }
  const [picked, setPicked] = useState([]); // bulk delete, Edit day only
  // Compact (default): one row per visit. Spacious: every hour, empty slots included.
  const [spacious, setSpacious] = useState(() => {
    try {
      return localStorage.getItem(DENSITY_KEY) === 'spacious';
    } catch (e) {
      return false;
    }
  });
  const toggleDensity = () =>
    setSpacious((on) => {
      try {
        localStorage.setItem(DENSITY_KEY, on ? 'compact' : 'spacious');
      } catch (e) {
        // this session only
      }
      return !on;
    });
  const [bulkDel, setBulkDel] = useState(false);
  useEffect(() => {
    if (!arrange) setPicked([]);
  }, [arrange]);
  const togglePick = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const onAddAt = (startMin, durationMin = DEFAULT_VISIT_MIN) =>
    setAddSheet({ startMin, durationMin });

  // One recording at a time; patients join it (Record +) or leave it (Stop on their row).
  const recRef = useRef(null);
  const startedRef = useRef(false);
  const [recIds, setRecIds] = useState([]);
  const [savingIds, setSavingIds] = useState([]);
  const [recStatus, setRecStatus] = useState('');
  const [recError, setRecError] = useState('');
  const recording = recIds.length > 0;
  const [level, setLevel] = useState(0);
  const [micMenu, setMicMenu] = useState(false);
  const [mics, setMics] = useState({ devices: [], selectedId: '' });

  // Live mic level for the wave and the meter.
  useEffect(() => {
    if (!recording) {
      setLevel(0);
      setMicMenu(false);
      return undefined;
    }
    const id = setInterval(
      () => setLevel(recRef.current?.getLevel?.() || 0),
      80,
    );
    return () => clearInterval(id);
  }, [recording]);

  const loadMics = async () => {
    await recRef.current?.refreshMics?.();
    // The recorder's handle sees the new list only after it re-renders.
    setTimeout(
      () =>
        setMics(recRef.current?.getMics?.() || { devices: [], selectedId: '' }),
      50,
    );
  };
  useEffect(() => {
    if (recording) loadMics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);
  useEffect(() => {
    if (!micMenu) return undefined;
    const close = (e) =>
      !e.target.closest?.('.vs-micmenu, .vs-micchip') && setMicMenu(false);
    document.addEventListener('pointerdown', close, true);
    return () => document.removeEventListener('pointerdown', close, true);
  }, [micMenu]);

  useEffect(() => {
    if (recording && !startedRef.current) {
      startedRef.current = true;
      setRecError('');
      recRef.current?.start();
    } else if (!recording && startedRef.current) {
      startedRef.current = false;
      recRef.current?.stop();
    }
  }, [recording]);

  const onRecorderStatus = (s) => {
    setRecStatus(s);
    const failed =
      typeof s === 'string' &&
      !s.startsWith('Recording') &&
      s !== 'Uploading Audio...';
    if (failed && startedRef.current && !recRef.current?.isRecording()) {
      startedRef.current = false;
      setRecIds([]);
      setRecError(s);
    }
  };

  const startRecording = (ids) => !recording && setRecIds(ids);
  const addToRecording = (id) =>
    setRecIds((prev) =>
      prev.includes(id) || prev.length >= MAX_IN_RECORDING
        ? prev
        : [...prev, id],
    );
  const stopRow = (id) => {
    setRecIds((prev) => prev.filter((x) => x !== id));
    setSavingIds((prev) => [...prev, id]);
  };
  const stopAll = () => {
    setSavingIds((prev) => [...prev, ...recIds]);
    setRecIds([]);
  };

  const items = visitsOnDay(conversations, day).sort((a, b) => a.s - b.s);
  const isToday = isSameDay(day, new Date());

  const stateOf = (c) => {
    const id = c.conversationId;
    if (recIds.includes(id)) return 'rec';
    const st = String(c.status || '');
    if (id === comprehendingId) return 'writing';
    if (savingIds.includes(id) || st.startsWith('Transcribing'))
      return 'saving';
    if (st.startsWith('Thinking')) return 'writing';
    if (st.startsWith('AI Complete')) return 'done';
    if (c.secondsTranscribed > 0) return 'draft';
    return 'todo';
  };

  const open = items.filter((it) => stateOf(it.conversation) !== 'done');
  const toRecord = open.filter(
    (it) => stateOf(it.conversation) === 'todo',
  ).length;
  const toFill = open.filter(
    (it) => stateOf(it.conversation) === 'draft',
  ).length;
  const dayDone = items.length > 0 && open.length === 0;

  const openVisit = (id) => {
    if (!recording) onOpen(id);
  };

  // ---- row controls ----
  const micFor = (c) => {
    const id = c.conversationId;
    const st = stateOf(c);
    if (st === 'rec' || st === 'done') return null;
    if (recording) {
      return (
        <button
          type="button"
          className="vs-mic vs-addrec"
          title={`Add ${who(c)} to this recording`}
          aria-label={`Add ${who(c)} to this recording`}
          onClick={(e) => {
            e.stopPropagation();
            addToRecording(id);
          }}
        >
          <MicAdd />
        </button>
      );
    }
    const resume = c.secondsTranscribed > 0;
    return (
      <button
        type="button"
        className={`vs-mic${resume ? ' vs-resume' : ''}`}
        title={`${resume ? 'Resume recording' : 'Record'} ${who(c)}`}
        aria-label={`${resume ? 'Resume recording' : 'Record'} ${who(c)}`}
        onClick={(e) => {
          e.stopPropagation();
          startRecording([id]);
        }}
      >
        <Mic />
      </button>
    );
  };

  const fillFor = (c) =>
    canComprehend && stateOf(c) === 'draft' ? (
      <button
        type="button"
        className="vs-fillb"
        disabled={recording}
        title={
          recording ? 'Stop recording to open a visit' : 'Comprehend your note'
        }
        aria-label={`Comprehend ${who(c)}’s note`}
        onClick={(e) => {
          e.stopPropagation();
          if (!recording && !comprehendingId) onComprehend(c.conversationId);
        }}
      >
        <Spark />
      </button>
    ) : null;

  const stopFor = (c) => (
    <button
      type="button"
      className="vs-stopdot"
      aria-label={`Stop recording ${who(c)}`}
      onClick={(e) => {
        e.stopPropagation();
        stopRow(c.conversationId);
      }}
    >
      <Stop />
    </button>
  );

  const openMenu = (e, id) => {
    e.stopPropagation();
    if (menu && menu.id === id) {
      setMenu(null);
      return;
    }
    const card = e.currentTarget.closest('.vs-cx-card').getBoundingClientRect();
    // Right-click / long-press opens it at the pointer.
    const at =
      e.type === 'contextmenu'
        ? { left: e.clientX, bottom: e.clientY }
        : e.currentTarget.getBoundingClientRect();
    setMenu({
      id,
      left: Math.max(8, Math.min(at.left - card.left, card.width - 196)),
      top: at.bottom - card.top + 4,
    });
  };

  useEffect(() => {
    if (!menu) return undefined;
    const close = (e) => !e.target.closest?.('.vs-menu') && setMenu(null);
    const esc = (e) => e.key === 'Escape' && setMenu(null);
    document.addEventListener('pointerdown', close, true);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', close, true);
      document.removeEventListener('keydown', esc);
    };
  }, [menu]);

  // ---- edit times: drag a row onto an hour, a half hour, or another visit ----
  const dragRef = useRef(null);
  const dropAt = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const target =
      el &&
      el.closest('.vs-rows [data-t], .vs-rows [data-add], .vs-rows [data-at]');
    if (!target) return null;
    const min = Number(
      target.getAttribute('data-t') ??
        target.getAttribute('data-add') ??
        target.getAttribute('data-at'),
    );
    return Number.isFinite(min) ? { el: target, min } : null;
  };
  const onArrangeDown = (e, it) => {
    if (e.target.closest('.vs-pick')) return;
    if (e.pointerType !== 'mouse' && !e.target.closest('.vs-grip')) return;
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      it,
      row: e.currentTarget,
      x0: e.clientX,
      y0: e.clientY,
      moved: false,
      tgt: null,
    };
  };
  const onArrangeMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    if (!d.moved && Math.abs(e.clientY - d.y0) + Math.abs(e.clientX - d.x0) < 5)
      return;
    d.moved = true;
    d.row.classList.add('vs-lifted');
    const t = dropAt(e.clientX, e.clientY);
    if (d.tgt && (!t || d.tgt.el !== t.el))
      d.tgt.el.classList.remove('vs-droptarget');
    d.tgt = t;
    if (t) t.el.classList.add('vs-droptarget');
    setGhost({
      x: e.clientX + 14,
      y: e.clientY - 18,
      name: who(d.it.conversation),
      min: t ? t.min : d.it.s,
    });
  };
  const onArrangeUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    setGhost(null);
    if (!d) return;
    d.row.classList.remove('vs-lifted');
    if (d.tgt) d.tgt.el.classList.remove('vs-droptarget');
    if (d.moved && d.tgt && d.tgt.min !== d.it.s) {
      onReschedule(
        d.it.conversation.conversationId,
        d.tgt.min,
        d.tgt.min + (d.it.e - d.it.s),
      );
    }
  };

  // ---- the list ----
  const rowFor = (it, timeLeft = false) => {
    const c = it.conversation;
    const st = stateOf(c);
    const name = who(c);
    const type = visitTypeOf(c);
    if (st === 'rec') {
      const time = recStatus.startsWith('Recording: ')
        ? recStatus.slice(11)
        : '';
      return (
        <div
          key={c.conversationId}
          className="vs-row vs-live vs-rec1"
          data-t={it.s}
        >
          <div className="vs-rtop">
            <span className="vs-lead">
              <Eq level={level} />
            </span>
            <span className="vs-nm">{name}</span>
            <span className="vs-meta vs-reclab">
              {time ? `Recording ${time}` : 'Recording'}
            </span>
            <span className="vs-racts">
              <span className="vs-aslot">{stopFor(c)}</span>
            </span>
          </div>
        </div>
      );
    }
    const fill = arrange ? null : fillFor(c);
    const lead = (
      <span className="vs-lead">
        <span className="vs-grip" aria-hidden="true">
          <Grip />
        </span>
      </span>
    );
    const chip =
      st === 'todo' ? null : st === 'saving' || st === 'writing' ? (
        <span className="vs-rstat vs-writing">
          {STATUS_LABEL[st]}
          <span className="vs-prog" aria-hidden="true">
            <i />
          </span>
        </span>
      ) : (
        <span className={`vs-rstat vs-${st}`}>
          {st === 'done' ? <Check /> : <i className="vs-sdot" />}
          {STATUS_LABEL[st]}
        </span>
      );
    return (
      <div
        key={c.conversationId}
        className={`vs-row ${arrange ? 'vs-arr' : 'vs-tap'} vs-${st}${!arrange && !timeLeft ? ' vs-indent' : ''}`}
        data-t={it.s}
        tabIndex={0}
        role="button"
        title={
          recording && !arrange ? 'Stop recording to open a visit' : undefined
        }
        aria-label={`${name}, ${clockLabel(it.s)}. ${arrange ? 'Drag to a new time.' : 'Open this visit.'}`}
        onClick={() => !arrange && openVisit(c.conversationId)}
        onKeyDown={(e) =>
          e.key === 'Enter' && !arrange && openVisit(c.conversationId)
        }
        onPointerDown={arrange ? (e) => onArrangeDown(e, it) : undefined}
        onPointerMove={arrange ? onArrangeMove : undefined}
        onPointerUp={arrange ? onArrangeUp : undefined}
        onPointerCancel={arrange ? onArrangeUp : undefined}
        onContextMenu={
          arrange
            ? undefined
            : (e) => {
                e.preventDefault();
                openMenu(e, c.conversationId);
              }
        }
      >
        <div className="vs-rtop">
          {arrange && (
            <input
              type="checkbox"
              className="vs-pick"
              aria-label={`Select ${name}`}
              checked={picked.includes(c.conversationId)}
              onClick={(e) => e.stopPropagation()}
              onChange={() => togglePick(c.conversationId)}
            />
          )}
          {arrange && lead}
          <span className={`vs-ninfo${timeLeft ? ' vs-timed' : ''}`}>
            {/* The time sits on the name's line; type and status line up under the name. */}
            <span className="vs-nline">
              {timeLeft && timeCell(it.s)}
              <span className={`vs-nm${visitTitle(c) ? '' : ' vs-unset'}`}>
                {name}
              </span>
            </span>
            {(type || chip) && (
              <span className="vs-nsub">
                {type && <span className="vs-meta">{type}</span>}
                {chip}
              </span>
            )}
          </span>
          {/* Record sits at the right edge until Comprehend joins it. */}
          <span className="vs-racts">
            <span className="vs-aslot">{arrange ? null : micFor(c)}</span>
            {fill && <span className="vs-aslot">{fill}</span>}
          </span>
        </div>
      </div>
    );
  };

  const half = (min) => (
    <button
      key={min}
      type="button"
      className="vs-half"
      data-add={min}
      aria-label={`Add a visit at ${clockLabel(min)}`}
      onClick={() => onAddAt(min)}
    >
      <span className="vs-hplus" aria-hidden="true">
        +
      </span>
    </button>
  );

  // Record N together on a double-booked time; Stop all where the group started.
  const slotRec = (min, group) => {
    if (arrange) return null;
    if (recording) {
      const live = items.filter((it) =>
        recIds.includes(it.conversation.conversationId),
      );
      if (live.length < 2 || live[0].s !== min) return null;
      return (
        <button
          type="button"
          className="vs-slotrec vs-stopall"
          onClick={stopAll}
          aria-label={`Stop all ${live.length} recordings at once`}
        >
          <Stop />
          Stop all
        </button>
      );
    }
    const free = group.filter((it) => stateOf(it.conversation) === 'todo');
    if (free.length < 2) return null;
    return (
      <button
        type="button"
        className="vs-slotrec"
        aria-label={`Record the ${free.length} patients booked at ${clockLabel(min)} in one recording`}
        onClick={() =>
          startRecording(
            free
              .slice(0, MAX_IN_RECORDING)
              .map((it) => it.conversation.conversationId),
          )
        }
      >
        <People />
        {`Record ${free.length} together`}
      </button>
    );
  };

  const nowMin = isToday ? nowMinutes() : null;

  const timeCell = (min, cls = '') => {
    const [hm, ap] = clockLabel(min).split(' ');
    return (
      <span className={`vs-ctime${cls}`}>
        {hm}
        <small>{ap.toLowerCase()}</small>
      </span>
    );
  };

  // Spacious: every half-hour from the first visit to the last (or now). Empty
  // half-hours are a quiet line you can tap to add; finished runs fold to one.
  const SLOT = 30;
  const renderSpacious = () => {
    if (!items.length) return renderList();
    const floor = (m) => Math.floor(m / SLOT) * SLOT;
    const first = floor(items[0].s);
    let last = floor(items[items.length - 1].s);
    if (nowMin !== null && nowMin > last && nowMin < 24 * 60)
      last = floor(nowMin);
    const nowSlot =
      nowMin !== null && !dayDone && nowMin >= first ? floor(nowMin) : null;
    const slots = [];
    for (let m = first; m <= last; m += SLOT) {
      const vs = items.filter((it) => it.s >= m && it.s < m + SLOT);
      const done =
        vs.length && vs.every((it) => stateOf(it.conversation) === 'done');
      slots.push({
        m,
        vs,
        kind: !vs.length ? 'empty' : done ? 'done' : 'todo',
      });
    }
    const out = [];
    const nowMark = () => (
      <div key="now" className="vs-cnow" data-nowline aria-label="Now">
        {timeCell(nowMin, ' vs-nowt')}
        <span className="vs-cnowline" />
      </div>
    );
    // Each visit carries its own time on its line; an empty half-hour is one quiet line.
    const slot = ({ m, vs }) => {
      if (m % 60 === 0 && m !== first)
        out.push(<div key={`r-${m}`} className="vs-hrule" />);
      const isNow = m === nowSlot;
      if (!vs.length) {
        out.push(
          <button
            key={`s-${m}`}
            type="button"
            className="vs-tslot vs-sslot"
            data-at={m}
            aria-label={`Add a visit at ${clockLabel(m)}`}
            onClick={() => onAddAt(m)}
          >
            {timeCell(m)}
            <span className="vs-sp" />
            <span className="vs-splus" aria-hidden="true">
              +
            </span>
          </button>,
        );
        if (isNow) out.push(nowMark());
        return;
      }
      const rec = slotRec(m, vs);
      if (rec)
        out.push(
          <div key={`h-${m}`} className="vs-tslot vs-srec" data-at={m}>
            <span className="vs-sp" />
            {rec}
          </div>,
        );
      vs.forEach((it, k) => {
        if (isNow && it.s > nowMin && (k === 0 || vs[k - 1].s <= nowMin))
          out.push(nowMark());
        out.push(rowFor(it, true));
      });
      if (isNow && vs[vs.length - 1].s <= nowMin) out.push(nowMark());
    };
    let i = 0;
    while (i < slots.length) {
      if (slots[i].kind === 'todo' || slots[i].m === nowSlot) {
        slot(slots[i]);
        i += 1;
        continue;
      }
      let j = i;
      while (
        j < slots.length &&
        slots[j].kind !== 'todo' &&
        slots[j].m !== nowSlot
      )
        j += 1;
      const run = slots.slice(i, j);
      const firstDone = run.findIndex((x) => x.kind === 'done');
      if (firstDone < 0) {
        run.forEach(slot);
        i = j;
        continue;
      }
      run.slice(0, firstDone).forEach(slot);
      const folded = run.slice(firstDone);
      const start = folded[0].m;
      const end = folded[folded.length - 1].m + SLOT;
      const nDone = folded.reduce((n, x) => n + x.vs.length, 0);
      const key = `s${start}`;
      const isOpen = !!openFolds[key];
      out.push(
        <div
          key={`sf-${start}`}
          className={`vs-tslot vs-fin vs-cfold${start % 60 === 0 && start !== first ? ' vs-hourline' : ''}`}
          onClick={() => setOpenFolds((f) => ({ ...f, [key]: !f[key] }))}
        >
          <span className="vs-th">{gapRange(start, end)}</span>
          <span className="vs-tdone">
            <Check />
            {`${nDone} done`}
          </span>
          <span className="vs-sp" />
          <button
            type="button"
            className="vs-tfold"
            aria-expanded={isOpen}
            aria-label={`${isOpen ? 'Hide' : 'Show'} ${plural(nDone, 'finished visit', 'finished visits')}`}
          >
            <Chev />
          </button>
        </div>,
      );
      if (isOpen) folded.forEach(slot);
      i = j;
    }
    pushEnd(out);
    return out;
  };

  const renderList = () => {
    if (!items.length) {
      return (
        <p className="vs-emptyhint">
          {sync.available ? (
            <>
              Press <b>Sync Schedule</b> to bring your day in — or tap any time
              below to add one yourself.
            </>
          ) : (
            <>
              Nothing on this day yet. <b>Load your day</b> (the calendar +)
              takes your whole day as text, voice or a photo — or tap any time
              below to add one at a time.
            </>
          )}
        </p>
      );
    }
    let lo = DAY_FIRST;
    let hi = DAY_LAST;
    items.forEach((it) => {
      const h = Math.floor(it.s / 60) * 60;
      lo = Math.min(lo, h);
      hi = Math.max(hi, h);
    });
    const hours = [];
    for (let h = lo; h <= hi; h += 60) {
      const inHour = items.filter((it) => it.s >= h && it.s < h + 60);
      const times = [...new Set(inHour.map((it) => it.s))].sort(
        (a, b) => a - b,
      );
      hours.push({ h, times });
    }
    // "Now" sits under the last heading at or before the clock.
    let nowSlot = null;
    if (nowMin !== null) {
      hours.forEach(({ h, times }) =>
        (times.length ? times : [h]).forEach((m) => {
          if (m <= nowMin && (nowSlot === null || m > nowSlot)) nowSlot = m;
        }),
      );
    }
    const nowLine = (m) =>
      !dayDone && m === nowSlot ? (
        <div
          key={`now-${m}`}
          className="vs-nowline"
          data-nowline
          aria-label="Now"
        />
      ) : null;

    const out = [];
    let first = true;
    const head = (min, group) => {
      const nDone = group.filter(
        (it) => stateOf(it.conversation) === 'done',
      ).length;
      const allDone = nDone === group.length;
      const collapsed = nDone > 0 && !openFolds[min] && !arrange;
      out.push(
        <div
          key={`h-${min}`}
          className={`vs-tslot${allDone ? ' vs-fin' : ''}${min === nowSlot ? ' vs-now' : ''}${min % 60 === 0 && !first ? ' vs-hourline' : ''}`}
          data-at={min}
          style={nDone ? { cursor: 'pointer' } : undefined}
          onClick={
            nDone
              ? () => setOpenFolds((f) => ({ ...f, [min]: !f[min] }))
              : undefined
          }
        >
          <span className="vs-th">{clockLabel(min)}</span>
          {nDone > 0 && (
            <span className={`vs-tdone${allDone ? '' : ' vs-sm'}`}>
              <Check />
              {`${nDone} done`}
            </span>
          )}
          <span className="vs-sp" />
          {slotRec(min, group)}
          {nDone > 0 && (
            <button
              type="button"
              className="vs-tfold"
              aria-expanded={!collapsed}
              aria-label={`${collapsed ? 'Show' : 'Hide'} the ${plural(nDone, 'finished visit', 'finished visits')} at ${clockLabel(min)}`}
            >
              <Chev />
            </button>
          )}
        </div>,
      );
      out.push(nowLine(min));
      first = false;
      const shown = collapsed
        ? group.filter((it) => stateOf(it.conversation) !== 'done')
        : group;
      shown.forEach((it) => out.push(rowFor(it)));
    };
    const freeHalf = (min, rule) => {
      out.push(
        <div
          key={`f-${min}`}
          className={`vs-tslot vs-empty vs-freehalf${rule ? ' vs-hourline' : ''}`}
          data-at={min}
        >
          <span className="vs-th vs-sub">{clockLabel(min)}</span>
          <span className="vs-halves">{half(min)}</span>
        </div>,
      );
      out.push(nowLine(min));
    };
    const emptyHour = (h) => {
      out.push(
        <div
          key={`e-${h}`}
          className={`vs-tslot vs-empty${h === nowSlot ? ' vs-now' : ''}${first ? '' : ' vs-hourline'}`}
          data-at={h}
        >
          <span className="vs-th">{clockLabel(h)}</span>
          <span className="vs-halves">
            {half(h)}
            {half(h + 30)}
          </span>
        </div>,
      );
      out.push(nowLine(h));
      first = false;
    };

    if (arrange) {
      out.push(
        <div key="arr" className="vs-arrbar">
          <span className="vs-arrt">
            {picked.length ? (
              <b>{`${picked.length} selected`}</b>
            ) : (
              <>
                <b>Editing your day</b> · drag to a new time, or select to
                delete
              </>
            )}
          </span>
          {picked.length > 0 && (
            <button
              type="button"
              className="vs-arrdel"
              onClick={() => setBulkDel(true)}
            >
              <Trash />
              Delete
            </button>
          )}
          {sync.available && (
            <button
              type="button"
              className="vs-arrdone"
              onClick={() => setArrange(false)}
            >
              Done
            </button>
          )}
        </div>,
      );
    }

    let i = 0;
    while (i < hours.length) {
      const { h, times } = hours[i];
      if (!times.length) {
        if (dayDone) {
          i += 1;
          continue;
        }
        let j = i;
        while (j < hours.length && !hours[j].times.length) j += 1;
        if (j - i >= GAP_FOLD && !arrange) {
          const isOpen = !!openGaps[h];
          const range = gapRange(h, hours[j - 1].h);
          out.push(
            <div
              key={`g-${h}`}
              className={`vs-tslot vs-gap${first ? '' : ' vs-hourline'}`}
              onClick={() => setOpenGaps((g) => ({ ...g, [h]: !g[h] }))}
            >
              <span className="vs-th">{range}</span>
              <span className="vs-gapn">No visits scheduled</span>
              <span className="vs-sp" />
              <button
                type="button"
                className="vs-tfold"
                aria-expanded={isOpen}
                aria-label={`${isOpen ? 'Hide' : 'Show'} the empty hours from ${range}`}
              >
                <Chev />
              </button>
            </div>,
          );
          if (
            nowSlot !== null &&
            nowSlot >= h &&
            nowSlot < hours[j - 1].h + 60 &&
            !isOpen
          ) {
            out.push(
              <div
                key={`now-g-${h}`}
                className="vs-nowline"
                data-nowline
                aria-label="Now"
              />,
            );
          }
          first = false;
          if (isOpen) for (let k = i; k < j; k += 1) emptyHour(hours[k].h);
          i = j;
          continue;
        }
        emptyHour(h);
        i += 1;
        continue;
      }
      const t0 = times.filter((m) => m < h + 30);
      const t1 = times.filter((m) => m >= h + 30);
      const group = (m) => items.filter((it) => it.s === m);
      if (!t0.length && !dayDone) freeHalf(h, !first);
      t0.forEach((m) => head(m, group(m)));
      if (!t1.length && !dayDone) freeHalf(h + 30, false);
      t1.forEach((m) => head(m, group(m)));
      first = false;
      i += 1;
    }

    pushEnd(out);
    return out;
  };

  const pushEnd = (out) => {
    if (dayDone) {
      out.push(
        <div key="clean" className="vs-cleanslate" data-clean>
          <span className="vs-ck">
            <Check />
          </span>
          <h4>Day complete</h4>
          <p>
            {`All ${plural(items.length, 'note', 'notes')} filled. Nothing left for ${isToday ? 'today' : 'this day'}.`}
          </p>
          <button
            type="button"
            className="vs-up"
            onClick={(e) =>
              e.currentTarget
                .closest('.vs-rows')
                .scrollTo({ top: 0, behavior: 'smooth' })
            }
          >
            ↑ See the day
          </button>
        </div>,
      );
    } else if (!arrange) {
      out.push(
        <div key="end" className="vs-dayend">
          <span className="vs-endrule" />
          <p>
            <b>That’s the whole day.</b>
          </p>
          <p>
            {toRecord ? `${toRecord} to record` : ''}
            {toRecord && toFill ? ' · ' : ''}
            {toFill ? `${toFill} to comprehend` : ''}
            {' — then you’re at a clean slate.'}
          </p>
        </div>,
      );
    }
  };

  // Compact: one row per visit, time on the left; finished runs fold to one line.
  const renderCompact = () => {
    if (!items.length) return renderList();
    const out = [];
    let nowDone = nowMin === null || dayDone;
    const nowAt = (s) => {
      if (!nowDone && s > nowMin) {
        nowDone = true;
        out.push(
          <div key="now" className="vs-cnow" data-nowline aria-label="Now">
            {timeCell(nowMin, ' vs-nowt')}
            <span className="vs-cnowline" />
          </div>,
        );
      }
    };
    let i = 0;
    while (i < items.length) {
      const it = items[i];
      nowAt(it.s);
      if (stateOf(it.conversation) !== 'done') {
        out.push(rowFor(it, true));
        i += 1;
        continue;
      }
      let j = i;
      while (j < items.length && stateOf(items[j].conversation) === 'done')
        j += 1;
      const run = items.slice(i, j);
      const key = `c${it.s}`;
      const isOpen = !!openFolds[key];
      out.push(
        <div
          key={`cf-${it.s}`}
          className="vs-tslot vs-fin vs-cfold"
          onClick={() => setOpenFolds((f) => ({ ...f, [key]: !f[key] }))}
        >
          <span className="vs-th">
            {run.length > 1
              ? gapRange(it.s, run[run.length - 1].s)
              : clockLabel(it.s)}
          </span>
          <span className="vs-tdone">
            <Check />
            {`${run.length} done`}
          </span>
          <span className="vs-sp" />
          <button
            type="button"
            className="vs-tfold"
            aria-expanded={isOpen}
            aria-label={`${isOpen ? 'Hide' : 'Show'} ${plural(run.length, 'finished visit', 'finished visits')}`}
          >
            <Chev />
          </button>
        </div>,
      );
      if (isOpen) run.forEach((r) => out.push(rowFor(r, true)));
      i = j;
    }
    nowAt(Infinity);
    pushEnd(out);
    return out;
  };

  // ---- scroll: land on now; pills to get back to it and to what's still open above ----
  const rowsRef = useRef(null);
  const nowTarget = () => {
    const rows = rowsRef.current;
    if (!rows) return null;
    const el =
      rows.querySelector('[data-clean]') ||
      rows.querySelector('[data-nowline]');
    if (!el) return null;
    const anchor =
      el.matches('[data-nowline]') && el.previousElementSibling
        ? el.previousElementSibling
        : el;
    const off = el.matches('[data-nowline]') ? 34 : 0;
    const t =
      anchor.getBoundingClientRect().top -
      rows.getBoundingClientRect().top +
      rows.scrollTop -
      off;
    return Math.max(0, Math.round(t));
  };
  const goNow = () => {
    const t = nowTarget();
    if (t !== null) rowsRef.current.scrollTo({ top: t, behavior: 'smooth' });
  };
  useEffect(() => {
    const t = nowTarget();
    if (t !== null) rowsRef.current.scrollTop = t;
    onRowsScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day.getTime(), items.length > 0]);

  const onRowsScroll = () => {
    const rows = rowsRef.current;
    if (!rows) return;
    if (menu) setMenu(null);
    const t = nowTarget();
    const away =
      t !== null && Math.abs(rows.scrollTop - t) > 60
        ? rows.scrollTop > t
          ? 'up'
          : 'down'
        : null;
    const top = rows.getBoundingClientRect().top + 40;
    const early =
      isToday && !arrange
        ? [
            ...rows.querySelectorAll('.vs-row.vs-todo, .vs-row.vs-draft'),
          ].filter((r) => r.getBoundingClientRect().bottom < top).length
        : 0;
    const offset = rows.offsetTop;
    setPills((p) =>
      p.now === away && p.early === early && p.top === offset
        ? p
        : { now: away, early, top: offset },
    );
  };
  const goEarliest = () => {
    const rows = rowsRef.current;
    const r = rows && rows.querySelector('.vs-row.vs-todo, .vs-row.vs-draft');
    if (r)
      rows.scrollTo({
        top: Math.max(0, r.offsetTop - rows.offsetTop - 44),
        behavior: 'smooth',
      });
  };

  const { rel, date } = dayTitle(day);

  const menuVisit =
    menu && items.find((it) => it.conversation.conversationId === menu.id);
  const delVisit =
    delId !== null &&
    items.find((it) => it.conversation.conversationId === delId);
  const single =
    recIds.length === 1
      ? items.find((it) => it.conversation.conversationId === recIds[0])
      : null;

  return (
    <div className="vsched">
      <div className="vs-cx-card">
        <div className="vs-cx-r1">
          <h2>Schedule</h2>
          <span className="vs-syncrow">
            {sync.available && (
              <>
                <button
                  type="button"
                  className="vs-btn vs-sync"
                  title="Sync Schedule"
                  disabled={sync.busy}
                  onClick={() => sync.onSync()}
                >
                  <SyncIcon />
                  <span>{sync.busy ? 'Syncing…' : 'Sync Schedule'}</span>
                </button>
              </>
            )}
            {!sync.available && (
              <button
                type="button"
                className="vs-btn vs-bulkadd"
                title="Load your day"
                aria-label="Load your day"
                onClick={() => setLoadDay(true)}
              >
                <CalPlus />
              </button>
            )}
            <button
              type="button"
              className="vs-btn vs-editday"
              aria-pressed={arrange}
              aria-label={arrange ? 'Done editing the day' : 'Edit day'}
              title={arrange ? undefined : 'Edit day'}
              onClick={() => setArrange(!arrange)}
            >
              <Pencil />
              <span className={arrange ? undefined : 'vs-lbl'}>
                {arrange ? 'Done' : 'Edit day'}
              </span>
            </button>
          </span>
          <span className="vs-subline">
            <span className={`vs-dayct${dayDone ? ' vs-alldone' : ''}`}>
              {dayDone ? (
                <>
                  <Check />
                  {`All ${plural(items.length, 'note', 'notes')} filled`}
                </>
              ) : (
                <>
                  {plural(items.length, 'visit', 'visits')}
                  {toRecord > 0 && <b>{` · ${toRecord} to record`}</b>}
                  {toFill > 0 && <b>{` · ${toFill} to comprehend`}</b>}
                </>
              )}
            </span>
            <span className="vs-stampin">
              {` · ${sync.available ? sync.stamp : 'No calendar sync'}`}
            </span>
            <span className="vs-sp" />
            {!arrange && (
              <button
                type="button"
                className="vs-density"
                aria-pressed={spacious}
                aria-label={
                  spacious
                    ? 'Compact view: one line per visit'
                    : 'Spacious view: every hour, empty slots included'
                }
                title={spacious ? 'Compact view' : 'Spacious view'}
                onClick={toggleDensity}
              >
                <Density spacious={spacious} />
              </button>
            )}
          </span>
        </div>

        <div className="vs-statusbox">
          {recError && (
            <div className="vs-status vs-warn">
              <p className="vs-head">{recError}</p>
              <button
                type="button"
                className="vs-closex"
                aria-label="Close this message"
                onClick={() => setRecError('')}
              >
                ×
              </button>
            </div>
          )}
          {status}
        </div>

        <div className="vs-cx-daybar vs-daycard">
          <button
            type="button"
            className="vs-nav"
            aria-label="Previous day"
            onClick={() => onDayChange(addDays(day, -1))}
          >
            ‹
          </button>
          <span className="vs-sp" />
          <label className="vs-d vs-dlab" title="Pick a date">
            {rel && <b>{rel}</b>}
            <i>{rel ? ` · ${date}` : date}</i>
            <input
              type="date"
              aria-label="Pick a date"
              value={isoDate(day)}
              onChange={(e) => {
                const [y, m, d] = e.target.value.split('-').map(Number);
                if (y) onDayChange(new Date(y, m - 1, d));
              }}
            />
          </label>
          {!isToday && (
            <button
              type="button"
              className="vs-today"
              onClick={() => onDayChange(startOfDay(new Date()))}
            >
              <Jump />
              Today
            </button>
          )}
          <span className="vs-sp" />
          <button
            type="button"
            className="vs-nav"
            aria-label="Next day"
            onClick={() => onDayChange(addDays(day, 1))}
          >
            ›
          </button>
        </div>

        {recording && (
          <div className="vs-recbanner">
            <div className="vs-rb1">
              <Eq level={level} />
              <b>Recording</b>
              <span className="vs-rbnames">
                {items
                  .filter((it) =>
                    recIds.includes(it.conversation.conversationId),
                  )
                  .map((it) => who(it.conversation))
                  .join(', ')}
              </span>
              {recStatus.startsWith('Recording: ') && (
                <span className="vs-rbtime">{recStatus.slice(11)}</span>
              )}
              <span className="vs-sp" />
              <button type="button" className="vs-rbstop" onClick={stopAll}>
                Stop
              </button>
            </div>
            <button
              type="button"
              className="vs-micchip"
              aria-haspopup="listbox"
              aria-expanded={micMenu}
              onClick={() => {
                if (!micMenu) loadMics();
                setMicMenu(!micMenu);
              }}
            >
              <Mic />
              <span>{micLabel(mics)}</span>
              <Caret />
            </button>
            {micMenu && (
              <div className="vs-micmenu" role="listbox">
                {micChoices(mics).map((d) => (
                  <button
                    key={d.deviceId}
                    type="button"
                    role="option"
                    aria-selected={d.selected}
                    className={d.selected ? 'vs-on' : ''}
                    onClick={() => {
                      recRef.current?.setMic?.(d.deviceId);
                      setMics((m) => ({ ...m, selectedId: d.deviceId }));
                      setMicMenu(false);
                    }}
                  >
                    <span className="vs-mck">{d.selected && <Check />}</span>
                    <span className="vs-mcl">
                      {d.label}
                      {d.isDefault && <small>System default</small>}
                    </span>
                  </button>
                ))}
                <div className="vs-miclevel">
                  <Mic />
                  <span className="vs-meter">
                    <i style={{ width: `${Math.round(level * 100)}%` }} />
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {pills.early > 0 && (
          <button type="button" className="vs-earlypill" onClick={goEarliest}>
            {`↑ ${pills.early} earlier ${pills.early === 1 ? 'isn’t' : 'aren’t'} done`}
          </button>
        )}
        <div className="vs-rows" ref={rowsRef} onScroll={onRowsScroll}>
          {arrange
            ? renderList()
            : spacious
              ? renderSpacious()
              : renderCompact()}
        </div>
        {pills.now && !addSheet && !loadDay && !delId && (
          <button type="button" className="vs-nowpill" onClick={goNow}>
            {`${pills.now === 'up' ? '↑' : '↓'} ${dayDone ? 'Day complete' : 'Back to now'}`}
          </button>
        )}

        {menuVisit && (
          <div
            className="vs-menu"
            role="menu"
            style={{ left: menu.left, top: menu.top }}
          >
            <button
              type="button"
              role="menuitem"
              disabled={recording}
              onClick={() => {
                setMenu(null);
                openVisit(menu.id);
              }}
            >
              <Open />
              Open visit
            </button>
            {!recIds.includes(menu.id) && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenu(null);
                  setArrange(true);
                }}
              >
                <Move />
                Edit times…
              </button>
            )}
            <hr />
            <button
              type="button"
              role="menuitem"
              className="vs-danger"
              disabled={recIds.includes(menu.id)}
              onClick={() => {
                setMenu(null);
                setDelId(menu.id);
              }}
            >
              <Trash />
              Delete visit
            </button>
          </div>
        )}
      </div>

      {ghost && (
        <div className="vs-dragghost" style={{ left: ghost.x, top: ghost.y }}>
          <span>{ghost.name}</span>
          <span className="vs-gt">{clockLabel(ghost.min)}</span>
        </div>
      )}

      {delVisit && (
        <div
          className="vs-sheet-wrap"
          onClick={(e) => e.target === e.currentTarget && setDelId(null)}
        >
          <div className="vs-sheet">
            <div className="vs-sheet-head">
              <div>
                <h3>Delete this visit?</h3>
                <p className="vs-sub">{`${who(delVisit.conversation)} · ${clockLabel(delVisit.s)} · ${date}`}</p>
              </div>
              <span className="vs-sp" />
              <button
                type="button"
                className="vs-sheetx"
                aria-label="Keep it"
                onClick={() => setDelId(null)}
              >
                ×
              </button>
            </div>
            {delVisit.conversation.secondsTranscribed > 0 ? (
              <p className="vs-delwarn">
                {`This visit has a recording${stateOf(delVisit.conversation) === 'done' ? ' and a note that was written from it' : ''}. Deleting the visit deletes ${stateOf(delVisit.conversation) === 'done' ? 'them' : 'it'} too.`}
              </p>
            ) : (
              <p className="vs-editnote">
                Nothing has been recorded into this visit yet.
              </p>
            )}
            <div className="vs-delacts">
              <button
                type="button"
                className="vs-delno"
                onClick={() => setDelId(null)}
              >
                Keep it
              </button>
              <span className="vs-sp" />
              <button
                type="button"
                className="vs-delyes"
                onClick={() => {
                  onDelete(delId);
                  setDelId(null);
                }}
              >
                Delete visit
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkDel && picked.length > 0 && (
        <div
          className="vs-sheet-wrap"
          onClick={(e) => e.target === e.currentTarget && setBulkDel(false)}
        >
          <div className="vs-sheet">
            <div className="vs-sheet-head">
              <div>
                <h3>{`Delete ${plural(picked.length, 'visit', 'visits')}?`}</h3>
                <p className="vs-sub">{date}</p>
              </div>
              <span className="vs-sp" />
              <button
                type="button"
                className="vs-sheetx"
                aria-label="Keep them"
                onClick={() => setBulkDel(false)}
              >
                ×
              </button>
            </div>
            {(() => {
              const withAudio = items.filter(
                (it) =>
                  picked.includes(it.conversation.conversationId) &&
                  it.conversation.secondsTranscribed > 0,
              ).length;
              return withAudio ? (
                <p className="vs-delwarn">
                  {`${plural(withAudio, 'visit has', 'visits have')} a recording. Deleting deletes the recording and any note written from it.`}
                </p>
              ) : (
                <p className="vs-editnote">
                  Nothing has been recorded into these visits yet.
                </p>
              );
            })()}
            <div className="vs-delacts">
              <button
                type="button"
                className="vs-delno"
                onClick={() => setBulkDel(false)}
              >
                Keep them
              </button>
              <span className="vs-sp" />
              <button
                type="button"
                className="vs-delyes"
                onClick={() => {
                  picked.forEach((id) => onDelete(id));
                  setPicked([]);
                  setBulkDel(false);
                }}
              >
                {`Delete ${plural(picked.length, 'visit', 'visits')}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {loadDay && (
        <LoadDaySheet
          day={day}
          onParse={onParseDay}
          onClose={() => setLoadDay(false)}
          onCreate={async (list) => {
            for (const it of list) {
              // eslint-disable-next-line no-await-in-loop
              await onCreateVisit({
                name: it.name,
                startMin: it.min,
                durationMin: it.dur,
              });
            }
            setLoadDay(false);
            setArrange(true);
          }}
        />
      )}

      {addSheet && (
        <AddVisitSheet
          day={day}
          startMin={addSheet.startMin}
          durationMin={addSheet.durationMin}
          patients={patients}
          onClose={() => setAddSheet(null)}
          onCreate={async (visit) => {
            if (await onCreateVisit(visit)) setAddSheet(null);
          }}
        />
      )}

      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: 0,
          height: 0,
          overflow: 'hidden',
          opacity: 0,
        }}
      >
        <AudioRecorder
          ref={recRef}
          icon
          freezeChunkTargets
          continueTimer
          conversationID={recIds.length === 1 ? recIds[0] : null}
          conversationIds={recIds}
          isDisabled={!recording}
          seconds_transcribed={
            single ? single.conversation.secondsTranscribed || 0 : 0
          }
          refresh={() => {
            setSavingIds([]);
            onRefresh();
          }}
          setStatus={onRecorderStatus}
        />
      </div>
    </div>
  );
};

export default VisitsSchedule;
